import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import UploadIcon from "@mui/icons-material/CloudUploadOutlined";
import CheckIcon from "@mui/icons-material/CheckCircleOutlined";
import ErrorIcon from "@mui/icons-material/ErrorOutlineOutlined";
import PendingIcon from "@mui/icons-material/RadioButtonUncheckedOutlined";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  foyerApi,
  type IngestJobView,
  type IngestReadiness,
} from "../../http/foyer";
import {
  ingestStepState,
  type IngestPhase,
  type IngestStepState,
} from "@shared/ingest-phases";
import { uploadInBlocks } from "../../lib/blockUpload";
import { describeError, useToast } from "../toast";
import { Trans, useTranslation } from "react-i18next";
import AssetOverviewTab from "./AssetOverviewTab";
import SubtitlesTab from "./SubtitlesTab";
import { tokens } from "../../theme";
import { useFormat, type Formatters } from "../../i18n/format";
import type { AssetPlaybackBlocker } from "@shared/channel-playability";
import type { Availability } from "@shared/content-rights";
import type { ImagesInput } from "@shared/vod-images";
import type { LocalisedInput } from "@shared/vod-localised";

/**
 * Media files — upload a master and watch it become a playable title (`BE-17`).
 *
 * The panel from the design, built BEFORE the transcoder hand-off exists, on
 * one condition: **every phase, incident and timestamp it draws is real**. They
 * come from the job row and the phase machine that will still be running when
 * dispatch is wired. Nothing here is a mockup of state.
 *
 * ## The one thing a half-built pipeline must never do
 *
 * Leave a job in `queued` and let the operator infer why. There are two
 * reasons a job waits and they demand opposite reactions: Foyer is YIELDING to
 * the portal on the shared farm (working as designed), or Foyer has no
 * credential for the farm at all (unfinished). The server states which in
 * `readiness`, and this panel prints it. A screen that cannot tell those apart
 * is worse than no screen.
 *
 * ## Three tabs, from the design (`BE-32`)
 *
 * Översikt / Video / Textning. Video shipped first and alone, which left the
 * panel answering "what did the transcode do" and nothing else — while the
 * question an operator actually arrives with is "is this title ready", whose
 * answer is spread across four other panels and one fact (does any package
 * carry it) that has no home at all.
 *
 * ## Why the ladder is derived, not stored
 *
 * A phase's status comes from comparing it to the job's CURRENT phase and its
 * `phaseLog`, so the ladder cannot disagree with the row it describes. The
 * shipped portal stores a status per step and has two ladders that classify the
 * same job differently — that is the bug this avoids by construction.
 */

const LADDER = ["queued", "transcoding", "ingesting", "publishing"] as const;

// The derivation moved to `@shared/ingest-phases` (`CONTENT-9`) so the supplier
// shell's ladder and this one cannot classify the same job two ways - which is
// exactly the bug the shipped portal has, with two ladders that disagree.
type StepState = IngestStepState;
const stepState = (job: IngestJobView, phase: IngestPhase): StepState =>
  ingestStepState(job, phase);

function StepIcon({ state }: { state: StepState }) {
  if (state === "done")
    return <CheckIcon sx={{ fontSize: 18, color: tokens.success }} />;
  if (state === "failed")
    return <ErrorIcon sx={{ fontSize: 18, color: tokens.error }} />;
  if (state === "current")
    return <CircularProgress size={14} sx={{ mr: 0.25 }} />;
  return <PendingIcon sx={{ fontSize: 18, color: tokens.inkFaint }} />;
}

function shortTime(fmt: Formatters, iso: string | null): string {
  if (!iso) return "—";
  const ms = Date.parse(iso);
  return Number.isNaN(ms)
    ? "—"
    : fmt.dateTime(ms, { dateStyle: "short", timeStyle: "short" });
}

function JobCard({
  job,
  readiness,
}: {
  job: IngestJobView;
  readiness: IngestReadiness;
}) {
  const { t } = useTranslation("content");
  const fmt = useFormat();
  const waitingUndispatched =
    job.phase === "queued" && !job.encoreJobId && !readiness.ready;

  return (
    <Box
      sx={{
        border: `1px solid ${tokens.divider}`,
        borderRadius: 2,
        p: 2,
        mb: 2,
      }}
      data-testid={`ingest-job-${job.id}`}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="bodySm"
            sx={{ fontWeight: "fontWeightBold", color: tokens.ink }}
          >
            {job.sourceObjectName}
          </Typography>
          <Typography variant="micro" sx={{ color: tokens.inkMuted }}>
            {t("mediaFilesPanel.startedBy", {
              time: shortTime(fmt, job.createdAt),
              user: job.createdBy,
            })}
          </Typography>
        </Box>
        <Chip
          size="small"
          label={t(`mediaFilesPanel.phase.${job.phase}`)}
          color={
            job.phase === "completed"
              ? "success"
              : job.phase === "failed"
                ? "error"
                : "default"
          }
          variant={job.phase === "completed" ? "filled" : "outlined"}
        />
      </Stack>

      {job.phase === "transcoding" && job.progress !== null && (
        <LinearProgress
          variant="determinate"
          value={Math.min(100, Math.max(0, job.progress))}
          sx={{ mb: 1.5, borderRadius: 1 }}
        />
      )}

      <Stack spacing={1.25} sx={{ mb: 1 }}>
        {LADDER.map((phase) => {
          const state = stepState(job, phase);
          return (
            <Stack
              key={phase}
              direction="row"
              spacing={1.25}
              alignItems="flex-start"
            >
              <Box sx={{ pt: 0.25 }}>
                <StepIcon state={state} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="meta"
                  sx={{
                    fontWeight:
                      state === "waiting"
                        ? "fontWeightRegular"
                        : "fontWeightBold",
                    color: state === "waiting" ? tokens.inkMuted : tokens.ink,
                  }}
                >
                  {t(`mediaFilesPanel.ladder.${phase}.label`)}
                </Typography>
                {(state === "current" || state === "failed") && (
                  <Typography variant="micro" sx={{ color: tokens.inkMuted }}>
                    {t(`mediaFilesPanel.ladder.${phase}.detail`)}
                  </Typography>
                )}
              </Box>
            </Stack>
          );
        })}
      </Stack>

      {/* The distinction the whole panel exists to make. */}
      {waitingUndispatched && readiness.explanation && (
        <Alert
          severity="info"
          sx={{ typography: "meta", mt: 1 }}
          data-testid="ingest-not-connected"
        >
          {readiness.explanation}
        </Alert>
      )}

      {/* Stated on the JOB too, not only before it starts: by the time this is
          visible the work is done, and "why can nobody see this title?" is the
          question somebody will be asking. */}
      {job.entitlements.length === 0 && (
        <Alert
          severity="warning"
          sx={{ typography: "meta", mt: 1 }}
          data-testid="job-no-package"
        >
          {t("mediaFilesPanel.noPackage")}
        </Alert>
      )}

      {job.error && (
        <Alert severity="error" sx={{ typography: "meta", mt: 1 }}>
          {job.error}
        </Alert>
      )}

      {job.incidents.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography
            variant="micro"
            sx={{ fontWeight: "fontWeightBold", mb: 0.5 }}
          >
            {t("mediaFilesPanel.incidents")}
          </Typography>
          {job.incidents.map((incident, i) => (
            <Typography
              variant="micro"
              key={`${incident.code}-${i}`}
              sx={{ color: tokens.inkMuted }}
            >
              {t(
                incident.detail
                  ? "mediaFilesPanel.incidentDetail"
                  : "mediaFilesPanel.incident",
                {
                  time: shortTime(fmt, incident.occurredAt),
                  code: incident.code,
                  detail: incident.detail,
                  attempt: incident.attempt,
                }
              )}
            </Typography>
          ))}
        </Box>
      )}

      {job.manifestUrls?.hls && (
        <Typography
          variant="micro"
          sx={{
            fontFamily: "fontFamilyMono",
            color: tokens.inkMuted,
            mt: 1,
            wordBreak: "break-all",
          }}
        >
          {job.manifestUrls.hls}
        </Typography>
      )}
    </Box>
  );
}

export default function MediaFilesPanel({
  assetId,
  assetTitle,
  asset,
  blockers,
  availability,
  open,
  onClose,
}: {
  assetId: string;
  assetTitle: string;
  /**
   * The draft as an asset, plus the two verdicts the editor's own rail already
   * computed over it.
   *
   * Passed in rather than recomputed so the Översikt tab and the rail behind it
   * cannot disagree — and omitted entirely by a caller that has no draft, in
   * which case the tab is not offered rather than being drawn from guesses.
   */
  asset?: ImagesInput & LocalisedInput;
  blockers?: AssetPlaybackBlocker[];
  availability?: Availability;
  open: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const { t } = useTranslation(["content", "common"]);
  // Defaults to Översikt when there is one, and to Video when the caller
  // handed over no draft — a `value` MUI has no `Tab` for renders an
  // indicator over nothing and warns.
  const hasOverview = Boolean(asset && blockers && availability);
  const [tab, setTab] = useState<"overview" | "video" | "subtitles">(
    hasOverview ? "overview" : "video"
  );
  const [jobs, setJobs] = useState<IngestJobView[] | null>(null);
  const [readiness, setReadiness] = useState<IngestReadiness | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [upStatus, setUpStatus] = useState<{
    pct: number | null;
    label: string;
  } | null>(null);
  const [packages, setPackages] = useState<Array<{ id: string; name: string }>>(
    []
  );
  const [chosen, setChosen] = useState<string[]>([]);
  const fileInput = useRef<HTMLInputElement | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    try {
      const answer = await foyerApi.listIngestJobs(assetId);
      setJobs(answer.jobs);
      setReadiness(answer.readiness);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [assetId]);

  useEffect(() => {
    if (!open) return;
    // The packages an ingest can join. Fetched once per opening: a title that
    // joins none is invisible to every guest, so this list is not optional
    // decoration.
    foyerApi
      .listIngestEntitlements()
      .then((a) => setPackages(a.collections))
      .catch(() => setPackages([]));
    void load();
    // Polled while the panel is open and stopped when it is not: an ingest is
    // minutes-to-hours long and a socket for it would outlive the dialog.
    timer.current = setInterval(() => void load(), 10_000);
    return () => {
      if (timer.current) clearInterval(timer.current);
      timer.current = null;
    };
  }, [open, load]);

  /**
   * Pick a file, and the whole registration happens around it.
   *
   * Register -> mint a scoped write URL -> PUT the blocks -> tell the server the
   * bytes landed. The job row exists from the first step, so a browser that
   * dies mid-upload leaves a visible queued job with no source rather than
   * nothing at all - and the poller skips exactly that state without failing it.
   */
  async function upload(file: File) {
    setError(null);
    setStarting(true);
    setUpStatus({ pct: null, label: t("mediaFilesPanel.status.registering") });
    try {
      const { job } = await foyerApi.startIngest(assetId, {
        extension: "mp4",
        entitlements: chosen,
      });
      setUpStatus({
        pct: null,
        label: t("mediaFilesPanel.status.askingStorage"),
      });
      const { uploadUrl } = await foyerApi.ingestUploadUrl(job.id);

      await uploadInBlocks(uploadUrl, file, {
        contentType: "video/mp4",
        onProgress: (sent, total) =>
          setUpStatus({
            pct: Math.round((sent / total) * 100),
            label: t("mediaFilesPanel.status.uploading", {
              sent: Math.round(sent / 1e6),
              total: Math.round(total / 1e6),
            }),
          }),
      });

      // The server checks the blob itself. "I finished" from a client that lost
      // its last block would otherwise put a truncated file on a serial farm.
      setUpStatus({ pct: 100, label: t("mediaFilesPanel.status.verifying") });
      await foyerApi.ingestUploaded(job.id);
      toast.success(t("mediaFilesPanel.uploaded", { title: assetTitle }));
      await load();
    } catch (e) {
      const err = e as { code?: string; message: string };
      toast.error(
        describeError(t, "uploadVideoFile", e, { title: assetTitle })
      );
      setError(
        err.code === "ingest_already_running" ||
          err.code === "storage_not_configured"
          ? t(`mediaFilesPanel.errors.${err.code}`)
          : err.message
      );
      // The job may exist without a source; showing it is more useful than
      // hiding the fact that a registration happened.
      await load();
    } finally {
      setStarting(false);
      setUpStatus(null);
    }
  }

  const live = jobs?.find(
    (j) => j.phase !== "completed" && j.phase !== "failed"
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 0.5 }}>
        {t("mediaFilesPanel.title")}
        <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
          {t("mediaFilesPanel.subtitle", { title: assetTitle })}
        </Typography>
      </DialogTitle>
      <Tabs
        value={tab}
        onChange={(_e, next: "overview" | "video" | "subtitles") =>
          setTab(next)
        }
        sx={{ px: 2, borderBottom: `1px solid ${tokens.divider}` }}
      >
        {/* Only when the caller handed over a draft. A summary assembled from
            guesses is the thing this tab exists to replace. */}
        {hasOverview ? (
          <Tab
            value="overview"
            label={t("mediaFilesPanel.tabs.overview")}
            data-testid="tab-overview"
          />
        ) : null}
        <Tab
          value="video"
          label={t("mediaFilesPanel.tabs.video")}
          data-testid="tab-video"
        />
        <Tab
          value="subtitles"
          label={t("mediaFilesPanel.tabs.subtitles")}
          data-testid="tab-subtitles"
        />
      </Tabs>
      <DialogContent dividers>
        {tab === "overview" && asset && blockers && availability && (
          <AssetOverviewTab
            assetId={assetId}
            asset={asset}
            blockers={blockers}
            availability={availability}
            jobs={jobs}
          />
        )}

        {tab === "subtitles" && <SubtitlesTab jobs={jobs} onChanged={load} />}

        {tab === "video" && (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2, typography: "meta" }}>
                {error}
              </Alert>
            )}

            {readiness && !readiness.ready && (
              <Alert
                severity="warning"
                sx={{ mb: 2, typography: "meta" }}
                data-testid="ingest-readiness"
              >
                {readiness.explanation}
                {readiness.missing.length > 0 && (
                  <Box
                    sx={{
                      mt: 0.5,
                      fontFamily: "fontFamilyMono",
                      typography: "micro",
                    }}
                  >
                    {t("mediaFilesPanel.missing", {
                      list: readiness.missing.join(", "),
                    })}
                  </Box>
                )}
              </Alert>
            )}

            <Box
              sx={{
                border: `1px dashed ${tokens.divider}`,
                borderRadius: 2,
                p: 3,
                textAlign: "center",
                mb: 2,
                bgcolor: tokens.brandTintSoft,
              }}
            >
              <UploadIcon sx={{ fontSize: 28, color: tokens.inkMuted }} />
              <Typography
                variant="bodySm"
                sx={{ fontWeight: "fontWeightBold", mt: 0.5 }}
              >
                {t("mediaFilesPanel.masterFile")}
              </Typography>
              <Typography
                variant="micro"
                sx={{ color: tokens.inkMuted, mt: 0.5 }}
              >
                <Trans
                  t={t}
                  i18nKey="mediaFilesPanel.uploadHint"
                  values={{ file: `${assetId}.mp4` }}
                  components={{
                    mono: (
                      <Box
                        component="span"
                        sx={{ fontFamily: "fontFamilyMono" }}
                      />
                    ),
                  }}
                />
              </Typography>

              {/* The choice that decides whether anybody can ever see this title.
              Above the button on purpose: it is a decision, not a setting, and
              a decision made after the upload has started is made too late. */}
              {/* Rendered even when the list is EMPTY. Hiding it there was the
              first version, and it hid the warning with it: an estate with no
              packages configured is exactly the case where an operator most
              needs telling that nothing they ingest will be visible. */}
              <Box sx={{ mt: 1.5, textAlign: "left" }}>
                <Typography
                  variant="micro"
                  sx={{ fontWeight: "fontWeightBold", mb: 0.5 }}
                >
                  {t("mediaFilesPanel.packagesJoin")}
                </Typography>
                <Stack
                  direction="row"
                  spacing={0.75}
                  flexWrap="wrap"
                  useFlexGap
                >
                  {packages.map((p) => (
                    <Chip
                      key={p.id}
                      size="small"
                      label={p.name}
                      data-testid={`ingest-package-${p.id}`}
                      color={chosen.includes(p.id) ? "primary" : "default"}
                      variant={chosen.includes(p.id) ? "filled" : "outlined"}
                      onClick={() =>
                        setChosen((prev) =>
                          prev.includes(p.id)
                            ? prev.filter((x) => x !== p.id)
                            : [...prev, p.id]
                        )
                      }
                    />
                  ))}
                </Stack>
                {chosen.length === 0 && (
                  <Typography
                    variant="micro"
                    sx={{ color: tokens.warning, mt: 0.5 }}
                    data-testid="ingest-no-package-warning"
                  >
                    {packages.length === 0
                      ? t("mediaFilesPanel.noPackagesYet")
                      : t("mediaFilesPanel.noPackageSelected")}
                  </Typography>
                )}
              </Box>

              {/* Hidden input, visible button: a bare file input cannot be styled
              and cannot say what it will do with what you hand it. */}
              <input
                ref={fileInput}
                type="file"
                accept="video/mp4,.mp4"
                hidden
                data-testid="ingest-file"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (file) void upload(file);
                }}
              />

              {upStatus && (
                <Box sx={{ mt: 1.5 }}>
                  <LinearProgress
                    variant={
                      upStatus.pct === null ? "indeterminate" : "determinate"
                    }
                    value={upStatus.pct ?? 0}
                    sx={{ borderRadius: 1 }}
                  />
                  <Typography
                    variant="micro"
                    sx={{ color: tokens.inkMuted, mt: 0.5 }}
                    data-testid="ingest-upload-status"
                  >
                    {upStatus.label}
                  </Typography>
                </Box>
              )}

              <Button
                variant="contained"
                size="small"
                sx={{ mt: 1.5 }}
                disabled={Boolean(upStatus) || starting || Boolean(live)}
                onClick={() => fileInput.current?.click()}
                data-testid="ingest-start"
              >
                {live
                  ? t("mediaFilesPanel.alreadyRunning")
                  : t("mediaFilesPanel.chooseMaster")}
              </Button>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Typography
              variant="meta"
              sx={{ fontWeight: "fontWeightBold", mb: 1 }}
            >
              {t("mediaFilesPanel.ingestStatus")}
            </Typography>

            {jobs === null && (
              <Stack alignItems="center" sx={{ py: 3 }}>
                <CircularProgress size={22} />
              </Stack>
            )}

            {jobs?.length === 0 && (
              <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
                {t("mediaFilesPanel.nothingIngested")}
              </Typography>
            )}

            {jobs?.map((job) =>
              readiness ? (
                <JobCard key={job.id} job={job} readiness={readiness} />
              ) : null
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common:buttons.close")}</Button>
      </DialogActions>
    </Dialog>
  );
}
